import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import {
  FBoxContainer,
  FCheckbox,
  FProgressBar,
  FHTextarea,
  FTypography,
  FHSmartTextarea,
} from '../../../base';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { crisisPlanInitialState, crisisPlanSchema } from './validationSchema';
import { crisisPlanActions, treatmentPlanActions } from '../../../../store';
import { getAllInvalidFieldNames, validateSync } from '../../../../helpers';
import { Themes } from '../../../../constants';
import { TreatmentPlanPages } from '../constants/general';
import { FORTA_YELLOW } from '../../../../utils/colors';
import { targetDescriptionBlacklist } from '../../../../pages/constants';

import type { ICrisisPlan } from '../../../../utils/types';
import type { ChangeEvent } from 'react';

function CrisisPlan() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const isEditable = useTreatmentPlanEditable();
  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const {
    current: crisisPlanData,
    loading: crisisPlanLoading,
    treatmentPlanId: currentTPId,
  } = useSelector((state: any) => state.crisisPlans);
  const [values, setValues] = useState<ICrisisPlan | null>(null);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (!crisisPlanData || !treatmentPlan) return;
    if (crisisPlanData) {
      setValues(crisisPlanData);
    }
  }, [crisisPlanData, treatmentPlan]);

  // Create new crisis plan if not exists
  useEffect(() => {
    if (!treatmentPlan) return;
    if (currentTPId !== treatmentPlan?.id) {
      dispatch(crisisPlanActions.updateState({ key: 'current', data: null }));
    }

    // Check if there is persisted crisis plan data for current treatment plan
    if (crisisPlanData && currentTPId === treatmentPlan?.id) {
      setValues(crisisPlanData);
      if (crisisPlanData?.is_needed) {
        handleValidate(crisisPlanData);
      }
      return;
    }

    fetchCrisisPlan();
    dispatch(crisisPlanActions.updateState({ key: 'treatmentPlanId', data: treatmentPlan?.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan]);

  useEffect(() => {
    if (Object.keys(errors).length === 0) {
      dispatch(treatmentPlanActions.updateBadge({ [TreatmentPlanPages.CrisisPlan]: 0 }));
      return;
    }

    const errorCount = Object.keys(errors).filter((key) => errors[key]).length;
    dispatch(treatmentPlanActions.updateBadge({ [TreatmentPlanPages.CrisisPlan]: errorCount }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const fetchCrisisPlan = async () => {
    if (!treatmentPlan) return;
    const { payload: crisisPlan } = await dispatch(
      crisisPlanActions.getCrisisPlan({
        treatmentPlanId: treatmentPlan?.id,
        getAccessTokenSilently,
      }),
    );
    if (!crisisPlan?.id) {
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.CrisisPlan]: '!',
        }),
      );
      setValues({ ...crisisPlanInitialState, ...crisisPlan });
      return;
    } else {
      setValues(crisisPlan);
    }
    setErrors({});
  };

  const handleUpdateField = <Key extends keyof ICrisisPlan>(
    key: Key,
    value: ICrisisPlan[Key],
    runValidations = true,
  ) => {
    if (!values) return;
    const data = { ...values, [key]: value };
    setValues((prev) => (prev ? { ...prev, [key]: value } : prev));
    if (runValidations) {
      handleValidate(data);
    }
    dispatch(crisisPlanActions.updateEditing({ treatmentPlanId: treatmentPlan?.id, data }));
  };

  const handleSave = () => {
    if (!treatmentPlan || !values) return;

    const isValid = validateSync(crisisPlanSchema, values);
    if (!isValid) {
      dispatch(
        crisisPlanActions.updateEditing({ treatmentPlanId: treatmentPlan?.id, data: values }),
      );
    } else if (values?.id) {
      dispatch(
        crisisPlanActions.updateCrisisPlan({
          treatmentPlanId: treatmentPlan?.id,
          data: values,
          getAccessTokenSilently,
        }),
      );
    } else {
      dispatch(
        crisisPlanActions.createCrisisPlan({
          treatmentPlanId: treatmentPlan?.id,
          data: values,
          getAccessTokenSilently,
        }),
      );
    }
  };

  const handleCreateCrisisPlan = async (value: boolean, data: ICrisisPlan | null = values) => {
    const { payload: newCrisisPlan } = await dispatch(
      crisisPlanActions.createCrisisPlan({
        treatmentPlanId: treatmentPlan?.id,
        data: data
          ? data
          : {
              is_needed: value,
            },
        getAccessTokenSilently,
      }),
    );
    if (newCrisisPlan) {
      setValues(newCrisisPlan);
      dispatch(
        treatmentPlanActions.updateBadge({
          [TreatmentPlanPages.CrisisPlan]: 0,
        }),
      );
    }
  };

  const handleSaveCheck = async (value: boolean) => {
    if (!treatmentPlan || !values) return;

    handleUpdateField('is_needed', value, false);

    // If Crisis plan doesn't exists, create crisis plan with is_needed = false
    if (!values?.id && value === false) {
      await handleCreateCrisisPlan(value, null);
      return;
    }

    const data = { ...values, is_needed: value };
    const isValid = validateSync(crisisPlanSchema, data);
    if (value && !isValid) {
      dispatch(crisisPlanActions.updateEditing({ treatmentPlanId: treatmentPlan?.id, data }));
      return;
    }

    // If Crisis plan doesn't exists and crisis plan data is valid and is_needed = true, create crisis plan
    if (isValid && !values?.id) {
      await handleCreateCrisisPlan(value, data);
      return;
    }

    if (values?.id) {
      dispatch(
        crisisPlanActions.updateCrisisPlan({
          treatmentPlanId: treatmentPlan?.id,
          data: value
            ? {
                ...values,
                is_needed: value,
              }
            : {
                is_needed: value,
              },
          getAccessTokenSilently,
        }),
      );
    }
    setErrors({});
  };

  const handleValidate = (data: any = values) => {
    if (!data) return;

    const invalidFields = getAllInvalidFieldNames(crisisPlanSchema, data);
    setErrors(invalidFields);
  };

  const handleBlur = (_key: string) => {
    handleSave();
  };

  const checkBoxFieldError = !values?.id && values?.is_needed === undefined && !crisisPlanLoading;

  if (!treatmentPlan?.id) {
    return <></>;
  }

  return (
    <FBoxContainer colorTheme={Themes.Primary} title="Crisis plan" sx={{ mt: 3 }}>
      {crisisPlanLoading && <FProgressBar />}
      <Box sx={{ display: 'flex' }}>
        <FTypography
          fontWeight={'bold'}
          mr={2}
          ml={checkBoxFieldError ? 1 : 0}
          error={checkBoxFieldError}
        >
          A crisis plan for this client is{' '}
        </FTypography>
        <FCheckbox
          data-testid="crisisPlanNotNeededCheckbox"
          label="not needed"
          colorTheme={Themes.Primary}
          checked={values?.is_needed === false}
          disabled={!isEditable}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleSaveCheck(e.target.checked ? false : true)
          }
          sx={{ mr: 1, fontWeight: 'bold' }}
        />
        <FCheckbox
          data-testid="crisisPlanNeededOutLinedBelowCheckbox"
          label="needed and outlined below"
          colorTheme={Themes.Primary}
          checked={values?.is_needed === true}
          disabled={!isEditable}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            handleSaveCheck(e.target.checked ? true : false)
          }
          sx={{ fontWeight: 'bold' }}
        />
      </Box>
      {values && values.is_needed && (
        <Box sx={{ display: 'flex', mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                Crisis description and trigger:*
              </Typography>
              <FHTextarea
                data-testid="crisisDescriptionAndTriggerTextField"
                expandable={isEditable}
                colorTheme={Themes.Primary}
                disabled={!isEditable}
                minLength={10}
                value={values.crisis_description || ''}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  handleUpdateField('crisis_description', e.target.value)
                }
                onBlur={() => handleBlur('crisis_description')}
                onCloseExpandDialog={() => handleBlur('crisis_description')}
                error={errors['crisis_description']}
                helperText={errors['crisis_description'] ? 'Minimum 10 characters' : ''}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                Antecedent interventions/modifications:*
              </Typography>
              <FHSmartTextarea
                id="antecedentInterventionTextField"
                data-testid="antecedentInterventionTextField"
                expandable={isEditable}
                colorTheme={Themes.Primary}
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                disabled={!isEditable}
                value={values.antecedent_intervention || ''}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  handleUpdateField('antecedent_intervention', e.target.value)
                }
                onBlur={() => handleBlur('antecedent_intervention')}
                onCloseExpandDialog={() => handleBlur('antecedent_intervention')}
                error={errors['antecedent_intervention']}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                Precursors with action steps to prevent behavior:*
              </Typography>
              <FHSmartTextarea
                id="precursorTextField"
                data-testid="precursorTextField"
                expandable={isEditable}
                colorTheme={Themes.Primary}
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                disabled={!isEditable}
                value={values.precursor || ''}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  handleUpdateField('precursor', e.target.value)
                }
                onBlur={() => handleBlur('precursor')}
                onCloseExpandDialog={() => handleBlur('precursor')}
                error={errors['precursor']}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                Behavior intervention plan when escalation occurs:*
              </Typography>
              <FHSmartTextarea
                id="behaviorInterventionPlanTextField"
                data-testid="behaviorInterventionPlanTextField"
                expandable={isEditable}
                colorTheme={Themes.Primary}
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                disabled={!isEditable}
                value={values.behavior_intervention_plan || ''}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  handleUpdateField('behavior_intervention_plan', e.target.value)
                }
                onBlur={() => handleBlur('behavior_intervention_plan')}
                onCloseExpandDialog={() => handleBlur('behavior_intervention_plan')}
                error={errors['behavior_intervention_plan']}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                Consequence strategies for behavior prior to escalation:*
              </Typography>
              <FHSmartTextarea
                id="consequenceStrategyTextField"
                data-testid="consequenceStrategyTextField"
                expandable={isEditable}
                colorTheme={Themes.Primary}
                hightlightOnDetection
                heightlightColour={FORTA_YELLOW}
                blackListedWordsPhrases={targetDescriptionBlacklist}
                disabled={!isEditable}
                value={values.consequence_strategy || ''}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  handleUpdateField('consequence_strategy', e.target.value)
                }
                onBlur={() => handleBlur('consequence_strategy')}
                onCloseExpandDialog={() => handleBlur('consequence_strategy')}
                error={errors['consequence_strategy']}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography fontWeight={'bold'} textAlign="left" mb={1}>
                Follow-up steps:*
              </Typography>
              <FHTextarea
                data-testid="followUpStepTextField"
                expandable={isEditable}
                colorTheme={Themes.Primary}
                disabled={!isEditable}
                value={values.follow_up_step || ''}
                onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  handleUpdateField('follow_up_step', e.target.value)
                }
                onBlur={() => handleBlur('follow_up_step')}
                onCloseExpandDialog={() => handleBlur('follow_up_step')}
                error={errors['follow_up_step']}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </FBoxContainer>
  );
}

export default CrisisPlan;
